/**
 * Comenzi ESC/POS pentru imprimanta termica Datecs DPP-450 (203 dpi, 832 de
 * puncte pe linie). Imaginile folosesc acelasi format ca SDK-ul Datecs
 * (`Printer.printImage`): benzi de 24 de puncte trimise cu `ESC * 33`.
 */

const ESC = 0x1b;
const LF = 0x0a;
const FF = 0x0c;

/** Punctele pe milimetru ale capului termic (203 dpi). */
export const DOTS_PER_MM = 8;

/** Latimea capului: 104 mm. */
export const HEAD_WIDTH_DOTS = 832;

const BAND_HEIGHT = 24;

/**
 * Un pixel mai inchis decat pragul devine punct negru. Pragul e peste
 * jumatate ca liniile subtiri (ex. "AUCTION HOUSE" din sigla, la eticheta de
 * 70 × 40) sa nu se rupa: marginile lor netezite ies gri deschis.
 */
const INK_THRESHOLD = 180;

export const INITIALIZE = [ESC, 0x40];

/**
 * Avans pana la inceputul etichetei urmatoare. Imprimanta il ignora daca nu e
 * in modul etichete: BLACK MARK MODE pornit, plus USE GAP SENSOR pentru
 * etichetele separate prin spatii.
 */
export const FEED_TO_NEXT_LABEL = [FF];

/** Avans cu `lines` randuri de text, ca hartia sa treaca de lama de rupere. */
export function feedLines(lines: number): number[] {
    return [ESC, 0x64, lines];
}

/** Avans cu `dots` puncte (maxim 255). */
export function feedDots(dots: number): number[] {
    return [ESC, 0x4a, Math.max(0, Math.min(255, Math.round(dots)))];
}

/** Unde si cat din imagine ajunge pe hartie. */
export interface ImagePlacement {
    /** Puncte spre dreapta pe linia capului; ce iese din cele 832 se taie. */
    offsetX: number;
    /**
     * Randuri goale adaugate deasupra (pozitiv) sau randuri taiate de sus
     * (negativ): pe etichete, capul incepe sa printeze putin mai jos decat
     * marginea etichetei.
     */
    offsetY: number;
    /**
     * Fiecare banda avanseaza hartia cu 24 de puncte, chiar daca imaginea se
     * termina mai devreme. Pe etichete se taie de jos randurile care nu umplu
     * o banda intreaga, ca hartia sa nu treaca in spatiul dintre etichete
     * inainte de avansul la urmatoarea.
     */
    wholeBands: boolean;
}

/**
 * Imaginea unei etichete, ca benzi `ESC * 33`. Eticheta este monocroma in
 * tema termica, asa ca ajunge un prag: o difuzie a erorii ar adauga puncte
 * razlete pe marginile modulelor din codul QR.
 */
export function encodeImage(
    image: ImageData,
    placement: ImagePlacement,
): number[] {
    const { width, height, data } = image;
    const shiftX = Math.max(
        0,
        Math.min(HEAD_WIDTH_DOTS - 1, Math.round(placement.offsetX)),
    );
    const shiftY = Math.max(-height, Math.round(placement.offsetY));
    const columns = Math.min(HEAD_WIDTH_DOTS, shiftX + width);
    const visibleRows = height + shiftY;
    const rows = placement.wholeBands
        ? visibleRows - (visibleRows % BAND_HEIGHT)
        : visibleRows;

    const isInk = (column: number, row: number): boolean => {
        const x = column - shiftX;
        const y = row - shiftY;
        if (x < 0 || x >= width || y < 0 || y >= height) return false;

        const i = (y * width + x) * 4;
        if (data[i + 3] < 128) return false;

        const luminance =
            0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        return luminance < INK_THRESHOLD;
    };

    const out: number[] = [ESC, 0x33, BAND_HEIGHT];

    for (let top = 0; top < rows; top += BAND_HEIGHT) {
        out.push(ESC, 0x2a, 33, columns & 0xff, columns >> 8);

        for (let column = 0; column < columns; column++) {
            // Fiecare coloana are 3 octeti, cu bitul cel mai semnificativ sus.
            for (let slice = 0; slice < 3; slice++) {
                let byte = 0;
                for (let bit = 0; bit < 8; bit++) {
                    if (isInk(column, top + slice * 8 + bit)) {
                        byte |= 0x80 >> bit;
                    }
                }
                out.push(byte);
            }
        }

        out.push(LF);
    }

    // Inapoi la spatierea implicita a randurilor.
    out.push(ESC, 0x32);
    return out;
}

/** Lipeste bucatile unei lucrari intr-un singur buffer pentru USB. */
export function concat(parts: number[][]): Uint8Array {
    const total = parts.reduce((sum, part) => sum + part.length, 0);
    const job = new Uint8Array(total);

    let position = 0;
    parts.forEach((part) => {
        job.set(part, position);
        position += part.length;
    });

    return job;
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--2!./src/utils/escpos.ts